/**
 * FeedbackForm component for SnapHub PWA.
 *
 * Renders a compact feedback form with category, message, and optional
 * feature fields. Validates required fields on submit, shows inline
 * error messages, and calls feedbackService.submitFeedback() to send
 * the data. All UI texts are in German.
 *
 * Validates: Requirements 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7, 3.8, 7.3
 */

import type { FeedbackUserInput } from '../../shared/models/feedback-entry.model';
import { feedbackService } from './feedback.service';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FeedbackFormOptions {
  /** Called after feedback was successfully submitted (or queued). */
  onSuccess?: () => void;
}

// ---------------------------------------------------------------------------
// Constants – German UI texts
// ---------------------------------------------------------------------------

const LABEL_CATEGORY = 'Kategorie';
const LABEL_MESSAGE = 'Nachricht';
const LABEL_FEATURE = 'Feature (optional)';
const PLACEHOLDER_CATEGORY = 'Bitte wählen…';
const PLACEHOLDER_MESSAGE = 'Beschreiben Sie Ihr Feedback…';
const PLACEHOLDER_FEATURE = 'z.\u00A0B. Warenkorb, Scanner…';
const BUTTON_SUBMIT = 'Feedback senden';
const BUTTON_SUBMITTING = 'Wird gesendet…';
const ERROR_CATEGORY = 'Bitte wählen Sie eine Kategorie';
const ERROR_MESSAGE_REQUIRED = 'Bitte geben Sie eine Nachricht ein';
const ERROR_MESSAGE_MAX = 'Nachricht darf maximal 2000 Zeichen lang sein';

const MESSAGE_MAX_LENGTH = 2000;

// ---------------------------------------------------------------------------
// Category options
// ---------------------------------------------------------------------------

const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: PLACEHOLDER_CATEGORY },
  { value: 'idea', label: 'Idee' },
  { value: 'bug', label: 'Bug' },
  { value: 'question', label: 'Frage' },
];

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Create a `<label>` element. */
function createLabel(text: string, htmlFor: string): HTMLLabelElement {
  const label = document.createElement('label');
  label.textContent = text;
  label.htmlFor = htmlFor;
  return label;
}

/** Create an inline error element (hidden by default). */
function createErrorElement(id: string): HTMLElement {
  const el = document.createElement('span');
  el.id = id;
  el.className = 'feedback-form__error';
  el.setAttribute('role', 'alert');
  el.style.display = 'none';
  return el;
}

/** Show an error message on a field. */
function showFieldError(
  field: HTMLElement,
  errorEl: HTMLElement,
  message: string,
): void {
  field.classList.add('feedback-form__field--invalid');
  field.setAttribute('aria-invalid', 'true');
  errorEl.textContent = message;
  errorEl.style.display = '';
}

/** Clear the error state from a field. */
function clearFieldError(field: HTMLElement, errorEl: HTMLElement): void {
  field.classList.remove('feedback-form__field--invalid');
  field.removeAttribute('aria-invalid');
  errorEl.textContent = '';
  errorEl.style.display = 'none';
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Render the feedback form into the given container.
 *
 * @param container - The DOM element to render into (will be cleared first)
 * @param options   - Optional callbacks (onSuccess)
 */
export function renderFeedbackForm(
  container: HTMLElement,
  options: FeedbackFormOptions = {},
): void {
  // Clear previous content
  container.innerHTML = '';

  // -- Form wrapper --
  const form = document.createElement('form');
  form.className = 'feedback-form';
  form.noValidate = true;

  // -- Category field --
  const categoryGroup = document.createElement('div');
  categoryGroup.className = 'feedback-form__group';

  const categoryLabel = createLabel(LABEL_CATEGORY, 'feedback-category');
  const categorySelect = document.createElement('select');
  categorySelect.id = 'feedback-category';
  categorySelect.className = 'feedback-form__select';
  categorySelect.setAttribute('aria-describedby', 'feedback-category-error');
  categorySelect.required = true;

  for (const opt of CATEGORY_OPTIONS) {
    const option = document.createElement('option');
    option.value = opt.value;
    option.textContent = opt.label;
    if (opt.value === '') {
      option.disabled = true;
      option.selected = true;
    }
    categorySelect.appendChild(option);
  }

  const categoryError = createErrorElement('feedback-category-error');

  categoryGroup.appendChild(categoryLabel);
  categoryGroup.appendChild(categorySelect);
  categoryGroup.appendChild(categoryError);

  // -- Message field --
  const messageGroup = document.createElement('div');
  messageGroup.className = 'feedback-form__group';

  const messageLabel = createLabel(LABEL_MESSAGE, 'feedback-message');
  const messageTextarea = document.createElement('textarea');
  messageTextarea.id = 'feedback-message';
  messageTextarea.className = 'feedback-form__textarea';
  messageTextarea.placeholder = PLACEHOLDER_MESSAGE;
  messageTextarea.maxLength = MESSAGE_MAX_LENGTH;
  messageTextarea.rows = 4;
  messageTextarea.required = true;
  messageTextarea.setAttribute('aria-describedby', 'feedback-message-error');

  const messageError = createErrorElement('feedback-message-error');

  messageGroup.appendChild(messageLabel);
  messageGroup.appendChild(messageTextarea);
  messageGroup.appendChild(messageError);

  // -- Feature field (optional) --
  const featureGroup = document.createElement('div');
  featureGroup.className = 'feedback-form__group';

  const featureLabel = createLabel(LABEL_FEATURE, 'feedback-feature');
  const featureInput = document.createElement('input');
  featureInput.type = 'text';
  featureInput.id = 'feedback-feature';
  featureInput.className = 'feedback-form__input';
  featureInput.placeholder = PLACEHOLDER_FEATURE;

  featureGroup.appendChild(featureLabel);
  featureGroup.appendChild(featureInput);

  // -- Submit button --
  const submitBtn = document.createElement('button');
  submitBtn.type = 'submit';
  submitBtn.className = 'feedback-form__submit btn-primary';
  submitBtn.textContent = BUTTON_SUBMIT;

  // -- Assemble form --
  form.appendChild(categoryGroup);
  form.appendChild(messageGroup);
  form.appendChild(featureGroup);
  form.appendChild(submitBtn);
  container.appendChild(form);

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  function validate(): boolean {
    let valid = true;

    // Category
    if (!categorySelect.value) {
      showFieldError(categorySelect, categoryError, ERROR_CATEGORY);
      valid = false;
    } else {
      clearFieldError(categorySelect, categoryError);
    }

    // Message
    const msg = messageTextarea.value.trim();
    if (!msg) {
      showFieldError(messageTextarea, messageError, ERROR_MESSAGE_REQUIRED);
      valid = false;
    } else if (msg.length > MESSAGE_MAX_LENGTH) {
      showFieldError(messageTextarea, messageError, ERROR_MESSAGE_MAX);
      valid = false;
    } else {
      clearFieldError(messageTextarea, messageError);
    }

    return valid;
  }

  // Clear errors on input
  categorySelect.addEventListener('change', () => {
    clearFieldError(categorySelect, categoryError);
  });

  messageTextarea.addEventListener('input', () => {
    clearFieldError(messageTextarea, messageError);
  });

  // ---------------------------------------------------------------------------
  // Submit handler
  // ---------------------------------------------------------------------------

  form.addEventListener('submit', (e: Event) => {
    e.preventDefault();

    if (!validate()) return;

    // Build user input
    const input: FeedbackUserInput = {
      category: categorySelect.value as FeedbackUserInput['category'],
      message: messageTextarea.value.trim(),
    };

    const featureValue = featureInput.value.trim();
    if (featureValue) {
      input.feature_key = featureValue;
    }

    // Disable button + loading state
    submitBtn.disabled = true;
    submitBtn.textContent = BUTTON_SUBMITTING;

    feedbackService
      .submitFeedback(input)
      .then((result) => {
        if (result.success) {
          // Reset fields and notify caller
          resetFeedbackForm(container);
          options.onSuccess?.();
        } else {
          // Re-enable button on validation error from API
          submitBtn.disabled = false;
          submitBtn.textContent = BUTTON_SUBMIT;
        }
      })
      .catch(() => {
        // Unexpected error – re-enable button
        submitBtn.disabled = false;
        submitBtn.textContent = BUTTON_SUBMIT;
      });
  });
}

/**
 * Reset the feedback form inside the given container.
 * Clears all field values and removes validation errors.
 */
export function resetFeedbackForm(container: HTMLElement): void {
  const form = container.querySelector<HTMLFormElement>('.feedback-form');
  if (!form) return;

  // Reset native form state
  form.reset();

  // Ensure the placeholder option is selected again
  const select = form.querySelector<HTMLSelectElement>('#feedback-category');
  if (select) {
    select.value = '';
    const categoryError = form.querySelector<HTMLElement>('#feedback-category-error');
    if (categoryError) clearFieldError(select, categoryError);
  }

  // Clear textarea
  const textarea = form.querySelector<HTMLTextAreaElement>('#feedback-message');
  if (textarea) {
    textarea.value = '';
    const messageError = form.querySelector<HTMLElement>('#feedback-message-error');
    if (messageError) clearFieldError(textarea, messageError);
  }

  // Clear feature input
  const featureInput = form.querySelector<HTMLInputElement>('#feedback-feature');
  if (featureInput) {
    featureInput.value = '';
  }

  // Re-enable submit button
  const submitBtn = form.querySelector<HTMLButtonElement>('.feedback-form__submit');
  if (submitBtn) {
    submitBtn.disabled = false;
    submitBtn.textContent = BUTTON_SUBMIT;
  }
}
